import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, Image, TouchableOpacity, KeyboardAvoidingView, Platform} from 'react-native';
import {Text, TextInput, Button, Snackbar, Menu, IconButton, RadioButton} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import GradientHeader from '../../components/common/GradientHeader';
import {launchImageLibrary} from 'react-native-image-picker';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useInventoryCatalogStore} from '../../store/useInventoryCatalogStore';
import {useInventoryStore} from '../../store/useInventoryStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {inventoryApi} from '../../services/api/inventoryApi';
import {colors, spacing} from '../../theme';

const AddEditMaterialScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const existingItem = route.params?.item;
  const defaultOrgUnitId = route.params?.defaultOrgUnitId;
  const isEdit = !!existingItem;

  const {createMaterial, updateMaterial, uploadMaterialImages} = useInventoryCatalogStore();
  const {materialTypes, materialUnits, fetchMaterialTypes, fetchMaterialUnits} = useInventoryStore();
  const {orgUnits, fetchOrgUnits} = useOrgUnitStore();

  const [name, setName] = useState(existingItem?.name || '');
  const [materialTypeId, setMaterialTypeId] = useState(existingItem?.materialTypeId || null);
  const [sku, setSku] = useState(existingItem?.sku || '');
  const [materialUnitId, setMaterialUnitId] = useState(existingItem?.materialUnitId || null);
  const [sellingPrice, setSellingPrice] = useState(existingItem?.sellingPrice != null ? String(existingItem.sellingPrice) : '');
  const [purchasePrice, setPurchasePrice] = useState(existingItem?.purchasePrice != null ? String(existingItem.purchasePrice) : '');
  const [currentStock, setCurrentStock] = useState(existingItem?.currentStock != null ? String(existingItem.currentStock) : '0');
  const [orgUnitId, setOrgUnitId] = useState(defaultOrgUnitId || null);

  const [typeMenuVisible, setTypeMenuVisible] = useState(false);
  const [unitMenuVisible, setUnitMenuVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  // Image state
  const [mainImageFile, setMainImageFile] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState(existingItem?.mainImageUrl || null);
  const [additionalSlots, setAdditionalSlots] = useState(() => {
    if (existingItem?.additionalImageUrls?.length) {
      return existingItem.additionalImageUrls.map(uri => ({uri}));
    }
    return [];
  });
  const [imagesChanged, setImagesChanged] = useState(false);

  useEffect(() => {
    fetchMaterialTypes();
    fetchMaterialUnits();
    if (orgUnits.length === 0) {
      fetchOrgUnits();
    }
  }, [fetchMaterialTypes, fetchMaterialUnits, fetchOrgUnits, orgUnits.length]);

  // Auto-select if only one store
  useEffect(() => {
    if (!isEdit && !orgUnitId && orgUnits.length === 1) {
      setOrgUnitId(orgUnits[0].id);
    }
  }, [orgUnits, isEdit, orgUnitId]);

  // Fetch fresh material data when editing to get latest image URLs
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    inventoryApi.materials.get(existingItem.id).then(res => {
      if (cancelled) return;
      const fresh = res.data.data;
      if (fresh.mainImageUrl) {
        setMainImagePreview(prev => prev || fresh.mainImageUrl);
      }
      if (fresh.additionalImageUrls?.length) {
        setAdditionalSlots(prev =>
          prev.length > 0 ? prev : fresh.additionalImageUrls.map(uri => ({uri})),
        );
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isEdit, existingItem?.id]);

  const selectedTypeName = materialTypes.find(t => t.id === materialTypeId)?.name || t('inventory.materialType');
  const selectedUnitName = materialUnits.find(u => u.id === materialUnitId)?.name || t('inventory.materialUnit');

  const pickImage = useCallback(async (onPick) => {
    const result = await launchImageLibrary({
      mediaType: 'photo',
      maxWidth: 1024,
      maxHeight: 1024,
      quality: 0.8,
    });
    if (!result.didCancel && result.assets?.[0]) {
      onPick(result.assets[0]);
      setImagesChanged(true);
    }
  }, []);

  const pickMainImage = useCallback(() => {
    pickImage(asset => {
      setMainImageFile(asset);
      setMainImagePreview(asset.uri);
    });
  }, [pickImage]);

  const removeMainImage = useCallback(() => {
    setMainImageFile(null);
    setMainImagePreview(null);
    setImagesChanged(true);
  }, []);

  const pickAdditionalImage = useCallback(() => {
    pickImage(asset => {
      setAdditionalSlots(prev => [...prev, {file: asset, uri: asset.uri}]);
    });
  }, [pickImage]);

  const removeAdditionalImage = useCallback((index) => {
    setAdditionalSlots(prev => prev.filter((_, i) => i !== index));
    setImagesChanged(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!name.trim() || !materialTypeId) {
      setSnackbar(t('validation.required'));
      return;
    }
    if (!isEdit && !orgUnitId) {
      setSnackbar(t('inventory.storeRequired'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        materialTypeId,
        sku: sku.trim() || null,
        materialUnitId,
        sellingPrice: sellingPrice ? parseFloat(sellingPrice) : null,
      };

      let materialId;
      if (isEdit) {
        await updateMaterial(existingItem.id, payload);
        materialId = existingItem.id;
      } else {
        payload.purchasePrice = purchasePrice ? parseFloat(purchasePrice) : null;
        payload.currentStock = currentStock ? parseFloat(currentStock) : 0;
        payload.orgUnitId = orgUnitId;
        const created = await createMaterial(payload);
        materialId = created.id;
      }

      if (imagesChanged) {
        await uploadMaterialImages(materialId, mainImageFile, additionalSlots);
      }

      navigation.goBack();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [name, materialTypeId, sku, materialUnitId, sellingPrice, purchasePrice, currentStock, orgUnitId, isEdit, existingItem, createMaterial, updateMaterial, uploadMaterialImages, mainImageFile, additionalSlots, imagesChanged, navigation, t]);

  return (
    <View style={styles.container}>
      <GradientHeader
        title={isEdit ? t('inventory.editMaterial') : t('inventory.addMaterial')}
        onBack={() => navigation.goBack()}
      />

      <KeyboardAvoidingView
        style={{flex: 1}}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView style={styles.form} keyboardShouldPersistTaps="handled">
        {/* Store Selection - only for new materials */}
        {!isEdit && orgUnits.length > 0 && (
          <View style={styles.storeSection}>
            <Text style={styles.storeLabel}>{t('inventory.store')} *</Text>
            <View style={styles.storeOptions}>
              {orgUnits.map(store => (
                <TouchableOpacity
                  key={store.id}
                  style={[styles.storeChip, orgUnitId === store.id && styles.storeChipSelected]}
                  onPress={() => setOrgUnitId(store.id)}>
                  <RadioButton.Android
                    value={String(store.id)}
                    status={orgUnitId === store.id ? 'checked' : 'unchecked'}
                    onPress={() => setOrgUnitId(store.id)}
                    color={colors.primary}
                  />
                  <View style={styles.storeChipContent}>
                    <Text style={[styles.storeChipText, orgUnitId === store.id && styles.storeChipTextSelected]}>
                      {store.name}
                    </Text>
                    {store.city && <Text style={styles.storeChipCity}>{store.city}</Text>}
                  </View>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}

        <TextInput
          label={t('inventory.materialName')}
          value={name}
          onChangeText={setName}
          mode="outlined"
          style={styles.input}
        />

        <Menu
          visible={typeMenuVisible}
          onDismiss={() => setTypeMenuVisible(false)}
          anchor={
            <TextInput
              label={t('inventory.materialType')}
              value={selectedTypeName}
              mode="outlined"
              style={styles.input}
              right={<TextInput.Icon icon="menu-down" onPress={() => setTypeMenuVisible(true)} />}
              onPressIn={() => setTypeMenuVisible(true)}
              editable={false}
            />
          }>
          {materialTypes.map(mt => (
            <Menu.Item
              key={mt.id}
              title={mt.name}
              onPress={() => { setMaterialTypeId(mt.id); setTypeMenuVisible(false); }}
            />
          ))}
        </Menu>

        <TextInput
          label={t('inventory.sku')}
          value={sku}
          onChangeText={setSku}
          mode="outlined"
          style={styles.input}
        />

        <Menu
          visible={unitMenuVisible}
          onDismiss={() => setUnitMenuVisible(false)}
          anchor={
            <TextInput
              label={t('inventory.materialUnit')}
              value={selectedUnitName}
              mode="outlined"
              style={styles.input}
              right={<TextInput.Icon icon="menu-down" onPress={() => setUnitMenuVisible(true)} />}
              onPressIn={() => setUnitMenuVisible(true)}
              editable={false}
            />
          }>
          {materialUnits.map(mu => (
            <Menu.Item
              key={mu.id}
              title={mu.name}
              onPress={() => { setMaterialUnitId(mu.id); setUnitMenuVisible(false); }}
            />
          ))}
        </Menu>

        <TextInput
          label={t('inventory.sellingPrice')}
          value={sellingPrice}
          onChangeText={setSellingPrice}
          keyboardType="decimal-pad"
          mode="outlined"
          left={<TextInput.Affix text="₹" />}
          style={styles.input}
        />

        <TextInput
          label={t('inventory.purchasePrice')}
          value={purchasePrice}
          onChangeText={isEdit ? undefined : setPurchasePrice}
          keyboardType="decimal-pad"
          mode="outlined"
          left={<TextInput.Affix text="₹" />}
          style={styles.input}
          disabled={isEdit}
        />
        {isEdit && (
          <Text style={styles.hintText}>{t('inventory.purchasePriceReadonly')}</Text>
        )}

        <TextInput
          label={t('inventory.currentStock')}
          value={currentStock}
          onChangeText={isEdit ? undefined : setCurrentStock}
          keyboardType="decimal-pad"
          mode="outlined"
          style={styles.input}
          disabled={isEdit}
        />
        {isEdit && (
          <Text style={styles.hintText}>{t('inventory.stockReadonly')}</Text>
        )}

        {/* Images Section */}
        <Text style={styles.sectionHeader}>{t('inventory.mainImage')}</Text>
        <TouchableOpacity style={styles.mainImageBlock} onPress={pickMainImage} activeOpacity={0.7}>
          {mainImagePreview ? (
            <View style={styles.imagePreviewContainer}>
              <Image source={{uri: mainImagePreview}} style={styles.mainImagePreview} />
              <IconButton
                icon="close-circle"
                size={22}
                iconColor={colors.error}
                style={styles.removeImageBtn}
                onPress={removeMainImage}
              />
            </View>
          ) : (
            <View style={styles.imagePlaceholder}>
              <Icon name="camera-plus-outline" size={32} color={colors.textSecondary} />
              <Text style={styles.uploadText}>{t('inventory.tapToUpload')}</Text>
            </View>
          )}
        </TouchableOpacity>

        <Text style={styles.sectionHeader}>{t('inventory.additionalImages')}</Text>
        <View style={styles.additionalRow}>
          {additionalSlots.map((slot, index) => (
            <View key={index} style={styles.additionalSlot}>
              <Image source={{uri: slot.uri}} style={styles.additionalImage} />
              <IconButton
                icon="close-circle"
                size={20}
                iconColor={colors.error}
                style={styles.removeAdditionalBtn}
                onPress={() => removeAdditionalImage(index)}
              />
            </View>
          ))}
          {additionalSlots.length < 3 && (
            <TouchableOpacity style={styles.additionalAddBlock} onPress={pickAdditionalImage} activeOpacity={0.7}>
              <Icon name="plus" size={28} color={colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>

        <Button
          mode="contained"
          onPress={handleSave}
          loading={saving}
          disabled={saving}
          style={styles.saveButton}>
          {t('common.save')}
        </Button>
      </ScrollView>
      </KeyboardAvoidingView>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const IMAGE_SIZE = 90;

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  form: {flex: 1, padding: spacing.md},
  storeSection: {
    marginBottom: spacing.md,
    padding: spacing.md,
    backgroundColor: colors.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
  },
  storeLabel: {fontSize: 14, fontWeight: '600', color: colors.text, marginBottom: spacing.sm},
  storeOptions: {gap: spacing.xs},
  storeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingRight: spacing.sm,
  },
  storeChipSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primary + '10',
  },
  storeChipContent: {flex: 1},
  storeChipText: {fontSize: 14, color: colors.text},
  storeChipTextSelected: {color: colors.primary, fontWeight: '500'},
  storeChipCity: {fontSize: 12, color: colors.textSecondary},
  input: {marginBottom: spacing.sm},
  hintText: {fontSize: 12, color: colors.textSecondary, marginBottom: spacing.sm, marginTop: -4, paddingLeft: spacing.xs},
  sectionHeader: {fontSize: 14, fontWeight: '600', color: colors.text, marginTop: spacing.md, marginBottom: spacing.sm},
  mainImageBlock: {
    width: '100%',
    height: 160,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: colors.textSecondary,
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: spacing.sm,
  },
  imagePreviewContainer: {flex: 1},
  mainImagePreview: {width: '100%', height: '100%', resizeMode: 'cover'},
  removeImageBtn: {position: 'absolute', top: 0, right: 0, backgroundColor: 'rgba(255,255,255,0.8)', margin: 4},
  imagePlaceholder: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  uploadText: {fontSize: 12, color: colors.textSecondary, marginTop: 4},
  additionalRow: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.sm},
  additionalSlot: {width: IMAGE_SIZE, height: IMAGE_SIZE, borderRadius: 8, overflow: 'hidden'},
  additionalImage: {width: '100%', height: '100%', resizeMode: 'cover'},
  removeAdditionalBtn: {position: 'absolute', top: -4, right: -4, backgroundColor: 'rgba(255,255,255,0.8)', margin: 0},
  additionalAddBlock: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: colors.textSecondary,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  saveButton: {marginTop: spacing.md, marginBottom: spacing.xl},
});

export default AddEditMaterialScreen;
